import { FC } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { paths } from '@/constants'
import { useTranslate } from '@/utils/translate/translate-client'
import { RadialGradient } from '@/views/TicketsLandingView/NewTicketsLandingView/components/RadialGradient'

interface Props {
  isBasicGuildUser: boolean
  className?: string
}

const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
}

export const GuildUpsell: FC<Props> = ({ isBasicGuildUser, className }) => {
  const { t } = useTranslate('tickets')

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
      className={classNames(
        styles.layoutContainer,
        'relative mx-auto flex flex-col items-center px-6 py-12 text-center md:max-w-[640px] md:px-0 md:py-[120px]',
        className,
      )}
    >
      <RadialGradient
        className="absolute right-0 top-1/2 -z-10 -translate-y-1/2 translate-x-1/2 opacity-10"
        from="50% 50% at 50% 50%, #F29F3D 0.13%"
        to="rgba(242, 159, 61, 0) 100%"
        size="200%"
      />
      <div className="mx-auto flex max-w-[640px] flex-col gap-3 md:gap-6">
        <Text weight="bold" color="white" className="photon-title-md md:photon-heading-lg xl:photon-heading-xl block">
          {isBasicGuildUser
            ? t('upgradeYourBasicMembership', 'Upgrade Your Basic Membership to Premium')
            : t('enjoyTheatricalPremiumMemberTitle', 'Enjoy Each Angel Movie in Theaters as a Premium Guild Member')}
        </Text>
        <Text color="core-gray-400" className="photon-paragraph-sm md:photon-paragraph-xl block">
          {isBasicGuildUser
            ? t(
                'upgradeTheatricalPremiumMemberDescription',
                'Upgrade to Premium and get two complimentary tickets to every Angel Studios release—use them or gift them to friends or family.',
              )
            : t(
                'enjoyTheatricalPremiumMemberDescription',
                'Enjoy two complimentary tickets to every Angel Studios release as a Premium Guild Member.',
              )}
        </Text>
      </div>

      <Image
        src="v1743380525/guild-tickets-graphic_4x_vy1pkn.webp"
        width={160}
        height={240}
        alt="guild tickets"
        className="mt-8 md:mt-10"
      />
      <Text color="white" className="photon-title-sm md:photon-title-xs mt-8 block md:mt-10">
        {isBasicGuildUser
          ? t('upgradeAccountFor8MorePer', 'Upgrade your account for $8 more per month.')
          : t('annualMembershipAsLowAs', 'Annual Membership as low as $15 per month.')}
      </Text>

      {!isBasicGuildUser && (
        <LinkButton
          external
          size="md"
          href="/guild/join"
          className=" mt-10 w-full !border-[#FFA41C] !bg-[#FFA41C] md:mt-12 md:w-fit"
          variant="white"
        >
          {t('getStarted', 'Get Started')}
        </LinkButton>
      )}

      {isBasicGuildUser && (
        <div className="mx-auto mt-10 flex w-full flex-col gap-2 md:mt-12 md:w-fit">
          <LinkButton external size="md" href={paths.account.guildMembership} className="w-full" variant="white">
            {t('upgradeMyMembership', 'Upgrade my Membership')}
          </LinkButton>

          <LinkButton
            external
            size="md"
            href={paths.account.guildMembership}
            className="w-full border-transparent text-white"
            variant="plain"
          >
            {t('manageMembership', 'Manage Membership')}
          </LinkButton>
        </div>
      )}
    </motion.section>
  )
}
